// src/components/tags/TagDetails.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  fetchTag,
  deleteTag,
  fetchTagStats,
  clearTagError,
} from '../../store/actions/tagActions';
import LoadingSpinner from '../common/LoadingSpinner';
import Alert from '../common/Alert';
import ConfirmDialog from '../common/ConfirmDialog';
import Tabs from '../common/Tabs';

const TagDetails = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const { 
    selectedTag: tag, 
    loading, 
    error,
    stats 
  } = useSelector((state) => state.tags);

  const [activeTab, setActiveTab] = useState('info');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const typeLabels = {
    GENERAL: 'Général',
    FAQ: 'FAQ',
    BLOG: 'Blog',
    TOPIC: 'Sujet',
    SKILL: 'Compétence',
  };

  // Styles object
  const styles = {
    container: {
      maxWidth: '1280px',
      margin: '0 auto',
      padding: '2rem 1rem',
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '2rem',
    },
    headerLeft: {
      display: 'flex',
      alignItems: 'center',
      gap: '1rem',
    },
    backButton: {
      padding: '0.5rem',
      backgroundColor: '#f3f4f6',
      border: 'none',
      borderRadius: '0.375rem',
      cursor: 'pointer',
      color: '#374151',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'background-color 0.2s',
    },
    backButtonHover: {
      backgroundColor: '#e5e7eb',
    },
    title: {
      fontSize: '1.5rem',
      fontWeight: 700,
      color: '#111827',
      margin: 0,
    },
    headerActions: {
      display: 'flex',
      gap: '0.75rem',
    },
    actionButton: {
      padding: '0.5rem 1rem',
      border: 'none',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.5rem',
      transition: 'all 0.2s',
      textDecoration: 'none',
    },
    editButton: {
      backgroundColor: '#4f46e5',
      color: '#ffffff',
    },
    editButtonHover: {
      backgroundColor: '#4338ca',
    },
    deleteButton: {
      backgroundColor: '#dc2626',
      color: '#ffffff',
    },
    deleteButtonHover: {
      backgroundColor: '#b91c1c',
    },
    icon: {
      width: '1.25rem',
      height: '1.25rem',
    },
    content: {
      backgroundColor: '#ffffff',
      borderRadius: '0.5rem',
      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
      overflow: 'hidden',
    },
    hero: {
      padding: '2rem',
      backgroundColor: '#f9fafb',
      borderBottom: '1px solid #e5e7eb',
    },
    heroContent: {
      display: 'flex',
      alignItems: 'center',
      gap: '2rem',
    },
    tagIcon: {
      width: '80px',
      height: '80px',
      borderRadius: '50%',
      backgroundColor: '#e5e7eb',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '2.5rem',
      color: '#4b5563',
    },
    tagInfo: {
      flex: 1,
    },
    tagName: {
      fontSize: '2rem',
      fontWeight: 700,
      color: '#111827',
      marginBottom: '0.5rem',
    },
    tagMeta: {
      display: 'flex',
      gap: '2rem',
      flexWrap: 'wrap',
    },
    metaItem: {
      display: 'flex',
      flexDirection: 'column',
    },
    metaLabel: {
      fontSize: '0.75rem',
      textTransform: 'uppercase',
      color: '#6b7280',
      marginBottom: '0.25rem',
    },
    metaValue: {
      fontSize: '1rem',
      fontWeight: 500,
      color: '#111827',
    },
    badge: {
      display: 'inline-flex',
      padding: '0.25rem 0.75rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      borderRadius: '9999px',
    },
    activeBadge: {
      backgroundColor: '#d1fae5',
      color: '#065f46',
    },
    inactiveBadge: {
      backgroundColor: '#fee2e2',
      color: '#991b1b',
    },
    typeBadge: {
      backgroundColor: '#f3f4f6',
      color: '#374151',
    },
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
      gap: '1.5rem',
      marginTop: '2rem',
    },
    statCard: {
      backgroundColor: '#ffffff',
      padding: '1rem',
      borderRadius: '0.5rem',
      border: '1px solid #e5e7eb',
    },
    statLabel: {
      fontSize: '0.875rem',
      color: '#6b7280',
      marginBottom: '0.5rem',
    },
    statValue: {
      fontSize: '1.5rem',
      fontWeight: 600,
      color: '#111827',
    },
    tabContent: {
      padding: '2rem',
    },
    infoSection: {
      marginBottom: '2rem',
    },
    sectionTitle: {
      fontSize: '1.125rem',
      fontWeight: 600,
      color: '#111827',
      marginBottom: '1.5rem',
      paddingBottom: '0.5rem',
      borderBottom: '2px solid #f3f4f6',
    },
    infoList: {
      listStyle: 'none',
      padding: 0,
      margin: 0,
    },
    infoItem: {
      display: 'flex',
      padding: '0.75rem 0',
      borderBottom: '1px solid #f3f4f6',
    },
    infoLabel: {
      width: '120px',
      fontSize: '0.875rem',
      fontWeight: 500,
      color: '#6b7280',
    },
    infoValue: {
      flex: 1,
      fontSize: '0.875rem',
      color: '#111827',
    },
    itemsList: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
      gap: '1rem',
    },
    itemCard: {
      padding: '1rem',
      backgroundColor: '#f9fafb',
      borderRadius: '0.5rem',
      border: '1px solid #e5e7eb',
    },
    itemTitle: {
      fontSize: '0.875rem',
      fontWeight: 600,
      color: '#111827',
      marginBottom: '0.25rem',
    },
    itemLink: {
      color: '#2563eb',
      textDecoration: 'none',
    },
    itemMeta: {
      fontSize: '0.75rem',
      color: '#6b7280',
    },
    emptyState: {
      textAlign: 'center',
      padding: '3rem 1.5rem',
      color: '#6b7280',
      fontSize: '0.875rem',
    },
  };

  useEffect(() => {
    if (id) {
      dispatch(fetchTag(id));
      dispatch(fetchTagStats(id));
    }
  }, [dispatch, id]);

  const handleDelete = useCallback(async () => {
    try {
      await dispatch(deleteTag(id));
      navigate('/admin/tags');
    } catch (error) {
      console.error('Delete failed:', error);
    }
  }, [dispatch, id, navigate]);

  const formatDate = (date) => {
    if (!date) return '—';
    return new Date(date).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading && !tag) {
    return <LoadingSpinner fullScreen />;
  }

  if (!tag && !loading) {
    return (
      <div style={styles.container}>
        <Alert
          type="error"
          message="Tag non trouvé"
        />
        <Link to="/admin/tags" style={styles.backButton}>
          Retour à la liste
        </Link>
      </div>
    );
  }

  const tabs = [
    { id: 'info', label: 'Informations' },
    { id: 'faqs', label: `FAQs (${stats?.faqCount || 0})` },
    { id: 'blog', label: `Articles (${stats?.blogPostCount || 0})` },
    { id: 'documents', label: `Documents (${stats?.documentCount || 0})` },
  ];

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <button
            onClick={() => navigate('/admin/tags')}
            style={styles.backButton}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = styles.backButtonHover.backgroundColor;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = styles.backButton.backgroundColor;
            }}
          >
            <svg style={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
          </button>
          <h1 style={styles.title}>Détails du tag</h1>
        </div>
        <div style={styles.headerActions}>
          <Link
            to={`/admin/tags/${id}/edit`}
            style={{
              ...styles.actionButton,
              ...styles.editButton,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = styles.editButtonHover.backgroundColor;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = styles.editButton.backgroundColor;
            }}
          >
            <svg style={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            Modifier
          </Link>
          <button
            onClick={() => setShowDeleteConfirm(true)}
            style={{
              ...styles.actionButton,
              ...styles.deleteButton,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = styles.deleteButtonHover.backgroundColor;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = styles.deleteButton.backgroundColor;
            }}
          >
            <svg style={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
            Supprimer
          </button>
        </div>
      </div>

      {error && (
        <Alert
          type="error"
          message={error}
          onClose={() => dispatch(clearTagError())}
        />
      )}

      <div style={styles.content}>
        {/* Hero Section */}
        <div style={styles.hero}>
          <div style={styles.heroContent}>
            <div style={styles.tagIcon}>
              #
            </div>
            <div style={styles.tagInfo}>
              <h2 style={styles.tagName}>{tag.name}</h2>
              <div style={styles.tagMeta}>
                <div style={styles.metaItem}>
                  <span style={styles.metaLabel}>Slug</span>
                  <span style={styles.metaValue}>/{tag.slug}</span>
                </div>
                <div style={styles.metaItem}>
                  <span style={styles.metaLabel}>Type</span>
                  <span style={{ ...styles.badge, ...styles.typeBadge }}>
                    {typeLabels[tag.type] || tag.type}
                  </span>
                </div>
                <div style={styles.metaItem}>
                  <span style={styles.metaLabel}>Statut</span>
                  <span
                    style={{
                      ...styles.badge,
                      ...(tag.isActive ? styles.activeBadge : styles.inactiveBadge),
                    }}
                  >
                    {tag.isActive ? 'Actif' : 'Inactif'}
                  </span>
                </div>
              </div>
              {tag.description && (
                <p style={{ marginTop: '1rem', color: '#4b5563' }}>{tag.description}</p>
              )}
            </div>
          </div>

          {/* Stats Grid */}
          <div style={styles.statsGrid}>
            <div style={styles.statCard}>
              <div style={styles.statLabel}>FAQs</div>
              <div style={styles.statValue}>{stats?.faqCount || 0}</div>
            </div>
            <div style={styles.statCard}>
              <div style={styles.statLabel}>Articles de blog</div>
              <div style={styles.statValue}>{stats?.blogPostCount || 0}</div>
            </div>
            <div style={styles.statCard}>
              <div style={styles.statLabel}>Documents</div>
              <div style={styles.statValue}>{stats?.documentCount || 0}</div>
            </div>
            <div style={styles.statCard}>
              <div style={styles.statLabel}>Utilisations totales</div>
              <div style={styles.statValue}>
                {(stats?.faqCount || 0) + (stats?.blogPostCount || 0) + (stats?.documentCount || 0)}
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={setActiveTab}
        />

        {/* Tab Content */}
        <div style={styles.tabContent}>
          {activeTab === 'info' && (
            <div>
              {/* Description */}
              <div style={styles.infoSection}>
                <h3 style={styles.sectionTitle}>Description</h3>
                <p style={{ fontSize: '0.875rem', color: '#374151', lineHeight: '1.6' }}>
                  {tag.description || 'Aucune description'}
                </p>
              </div>

              {/* Metadata */}
              <div style={styles.infoSection}>
                <h3 style={styles.sectionTitle}>Métadonnées</h3>
                <ul style={styles.infoList}>
                  <li style={styles.infoItem}>
                    <span style={styles.infoLabel}>Créé le</span>
                    <span style={styles.infoValue}>{formatDate(tag.createdAt)}</span>
                  </li>
                  <li style={styles.infoItem}>
                    <span style={styles.infoLabel}>Modifié le</span>
                    <span style={styles.infoValue}>{formatDate(tag.updatedAt)}</span>
                  </li>
                  <li style={styles.infoItem}>
                    <span style={styles.infoLabel}>Fréquence d'utilisation</span>
                    <span style={styles.infoValue}>
                      {tag.usageCount || 0} fois
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          )}

          {activeTab === 'faqs' && (
            <div>
              {stats?.recentFaqs && stats.recentFaqs.length > 0 ? (
                <div style={styles.itemsList}>
                  {stats.recentFaqs.map((faq) => (
                    <div key={faq.id} style={styles.itemCard}>
                      <div style={styles.itemTitle}>
                        <Link to={`/admin/faqs/${faq.id}/details`} style={styles.itemLink}>
                          {faq.question}
                        </Link>
                      </div>
                      <div style={styles.itemMeta}>
                        Statut: {faq.status} • Vues: {faq.views}
                      </div>
                      <div style={styles.itemMeta}>
                        Créé le: {new Date(faq.createdAt).toLocaleDateString()}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={styles.emptyState}>
                  Aucune FAQ associée à ce tag
                </div>
              )}
            </div>
          )}

          {activeTab === 'blog' && (
            <div>
              {stats?.recentBlogPosts && stats.recentBlogPosts.length > 0 ? (
                <div style={styles.itemsList}>
                  {stats.recentBlogPosts.map((post) => (
                    <div key={post.id} style={styles.itemCard}>
                      <div style={styles.itemTitle}>
                        <Link to={`/admin/blog/${post.id}/details`} style={styles.itemLink}>
                          {post.title}
                        </Link>
                      </div>
                      <div style={styles.itemMeta}>
                        Statut: {post.status} • Vues: {post.views}
                      </div>
                      <div style={styles.itemMeta}>
                        Publié le: {post.publishedAt ? new Date(post.publishedAt).toLocaleDateString() : 'Non publié'}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={styles.emptyState}>
                  Aucun article associé à ce tag
                </div>
              )}
            </div>
          )}

          {activeTab === 'documents' && (
            <div>
              {stats?.recentDocuments && stats.recentDocuments.length > 0 ? (
                <div style={styles.itemsList}>
                  {stats.recentDocuments.map((doc) => (
                    <div key={doc.id} style={styles.itemCard}>
                      <div style={styles.itemTitle}>
                        <a href={doc.storagePath} target="_blank" rel="noopener noreferrer" style={styles.itemLink}>
                          {doc.originalName || doc.name}
                        </a>
                      </div>
                      <div style={styles.itemMeta}>
                        Type: {doc.fileType} • Taille: {(doc.fileSize / 1024).toFixed(2)} KB
                      </div>
                      <div style={styles.itemMeta}>
                        Ajouté le: {new Date(doc.createdAt).toLocaleDateString()}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={styles.emptyState}>
                  Aucun document associé à ce tag
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation */}
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDelete}
        title="Supprimer le tag"
        message={`Êtes-vous sûr de vouloir supprimer le tag "${tag?.name}" ?`}
        confirmText="Supprimer"
        cancelText="Annuler"
        type="danger"
      />
    </div>
  );
};

export default TagDetails;